import React, {ClassAttributes, InputHTMLAttributes} from 'react';
import {Icon} from "types/Icon";
import FieldHeader from "../Field/FieldHeader";
import {ExclamationCircleIcon} from "@heroicons/react/solid";
import FieldHelp from "../Field/FieldHelp";
import {classNames} from "../../../../services/string";

// Component ----

type CustomProps = {
  name: string
  label?: string | null
  icon?: Icon | null
  hint?: string | null
  leadingAddOn?: React.ReactNode
  help?: string | null
  error?: string | null
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>
  & ClassAttributes<HTMLInputElement>
  & CustomProps

function Input({label, icon: Icon, hint, leadingAddOn, help, error, ...props}: InputProps) {

  // Element IDs
  const descriptionId = `${props.name}-description`

  // Input classes
  const inputBaseClasses = 'block w-full rounded-lg sm:text-sm sm:leading-6'
  const inputRangeClasses = 'accent-brand'
  const inputNonRangeClasses = 'py-1.5 border-0 ring-1 ring-inset focus:ring-2 focus:ring-inset'
  const inputNormalClasses = 'text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:ring-brand'
  const inputDisabledClasses = 'text-gray-500 ring-gray-300 placeholder:text-gray-400 cursor-not-allowed'
  const inputErrorClasses = 'pr-10 text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-red-500'
  const inputWithIconClasses = 'pl-10'
  const inputWithLeadingAddOnClasses = 'pl-20'

  return (
    <div>
      {!!label && <FieldHeader fieldId={props.id || props.name} label={label} hint={hint} />}

      <div className={classNames(!!label && 'mt-1', props.type !== 'range' && 'shadow-sm', 'relative rounded-lg')}>

        {/* Leading icon */}
        {!!Icon &&
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Icon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
          </div>
        }

        {/* Leading add-on component */}
        {!!leadingAddOn &&
          leadingAddOn
        }

        {/* Main input */}
        <input
          id={props.id || props.name}
          aria-invalid={!!error}
          aria-describedby={descriptionId}
          className={classNames(
            inputBaseClasses,
            props.type === 'range'? inputRangeClasses : inputNonRangeClasses,
            !!Icon && inputWithIconClasses,
            !!leadingAddOn && inputWithLeadingAddOnClasses,
            !!error ? inputErrorClasses : props.disabled ? inputDisabledClasses : inputNormalClasses,
          )}
          {...props}
        />

        {/* Trailing icon */}
        {!!error &&
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
          </div>
        }
      </div>

      {/* Help text or error message */}
      {(!!help || !!error) &&
        <FieldHelp isError={!!error} id={descriptionId}>
          {error ?? help}
        </FieldHelp>
      }
    </div>
  );
}

export default Input;